import React, { useCallback, useEffect, useState } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { AlertTriangle, ChevronDown, ChevronRight, Paperclip, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { useAuth } from "../../contexts/AuthContext";
import {
  rrApi, CAUSE_LABELS, fmtDate,
} from "../../lib/rejectRemakeApi";
import {
  RRStatusBadge, RRTypeBadge, RR_STATUS_TONE,
} from "../etams/StatusBadge";
import { fetchProductionStages, stageLabel } from "../../lib/productionStages";
import ReportModal from "./ReportModal";
import EvidenceManager from "./EvidenceManager";
import { mintaTeks } from "../../lib/dialogAplikasi";

/**
 * Main Reject/Remake section — di-embed di WODetail.jsx.
 * Props:
 *   - wo: full WO detail object (dari GET /wo/{id}) — untuk stages + status guard
 *   - onWOChanged?: opsional callback jika ada perubahan yang memerlukan refetch WO parent
 */
export default function RejectRemakeSection({ wo, onWOChanged }) {
  const { profile, isOwner, isKepalaCabang } = useAuth();
  const roleCode = profile?.role?.code;
  const isAdminProduksi = roleCode === "ADMIN_PRODUKSI";

  // Role visibility (mirrors backend role matrix).
  const canRead = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI", "CS", "ADMIN_GUDANG"].includes(roleCode);
  const canWrite = isOwner || isKepalaCabang || isAdminProduksi;
  const canDelete = isOwner;

  // WO status guard for section visibility.
  const woActive = ["LOCKED", "IN_PROGRESS", "PENDING_REMAKE", "COMPLETED", "CANCELLED"].includes(wo?.status);
  // WO status guard for reporting — cocok dengan WO_STATUS_ALLOWED_FOR_REPORT
  // di backend/services/wo_reject_remake_service.py (K5: backend acuan).
  const canReport = canWrite && ["LOCKED", "IN_PROGRESS", "PENDING_REMAKE"].includes(wo?.status);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    if (!wo?.id) return;
    try {
      const r = await rrApi.list(wo.id);
      setItems(r.items || []);
    } catch (e) {
      // 403 = role not allowed (safe silent since section hidden anyway)
      if (e?.response?.status !== 403) {
        toast.error(apiErrorMessage(e, "Gagal memuat reject/remake."));
      }
    } finally {
      setLoading(false);
    }
  }, [wo?.id]);

  useEffect(() => { load(); }, [load]);
  // Polling ringan 15s.
  useEffect(() => {
    if (!wo?.id || !canRead || !woActive) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [wo?.id, canRead, woActive, load]);

  if (!canRead || !woActive) return null;

  const stat = items.reduce((acc, r) => {
    acc.total++;
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, { total: 0 });

  return (
    <>
      <Card className="p-4" data-testid="rr-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <h2 className="font-medium">Reject / Remake</h2>
            </div>
            <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
              <span data-testid="rr-count-total">{stat.total} laporan</span>
              {["REPORTED", "IN_REMAKE", "RESOLVED", "CANCELLED"].map(s => (
                stat[s] ? (
                  <span key={s}>· {stat[s]} {RR_STATUS_TONE[s].l}</span>
                ) : null
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={load} data-testid="rr-refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
            {canReport && (
              <Button size="sm" onClick={() => setReportOpen(true)} data-testid="rr-report-open">
                <Plus className="h-4 w-4 mr-1" /> Lapor Reject/Remake
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Memuat…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground italic border border-dashed border-border rounded p-3 text-center">
            Belum ada laporan reject/remake untuk WO ini.
          </p>
        ) : (
          <div className="space-y-2">
            {items.map(rr => (
              <RRRow
                key={rr.id}
                rr={rr}
                woId={wo.id}
                expanded={expandedId === rr.id}
                onToggle={() => setExpandedId(expandedId === rr.id ? null : rr.id)}
                canWrite={canWrite}
                canDelete={canDelete}
                isKepalaCabang={isKepalaCabang}
                isOwner={isOwner}
                onChange={() => { load(); onWOChanged?.(); }}
              />
            ))}
          </div>
        )}
      </Card>

      {canReport && (
        <ReportModal
          open={reportOpen}
          onOpenChange={setReportOpen}
          woId={wo.id}
          stages={wo.stages || []}
          onCreated={load}
        />
      )}
    </>
  );
}

/** Single reject/remake row — collapsed summary + expandable detail with actions + evidence. */
function RRRow({ rr, woId, expanded, onToggle, canWrite, canDelete, onChange, isKepalaCabang, isOwner }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoadingDetail(true);
    try {
      const r = await rrApi.get(woId, rr.id);
      setDetail(r);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat detail reject/remake."));
    } finally {
      setLoadingDetail(false);
    }
  }, [woId, rr.id]);

  useEffect(() => {
    if (expanded) loadDetail();
  }, [expanded, loadDetail]);

  const terminal = rr.status === "RESOLVED" || rr.status === "CANCELLED";
  // Batch 2.2: label tahap dari master. stageLabel() jatuh ke kode mentah
  // untuk tahap non-aktif, supaya reject WO lama tetap terbaca.
  const [stages, setStages] = useState([]);
  useEffect(() => {
    fetchProductionStages().then(setStages).catch((e) => { setStages([]); toast.error(apiErrorMessage(e, "Daftar tahap produksi gagal dimuat — muat ulang halaman.")); });
  }, []);

  return (
    <div className="border rounded-lg bg-card" data-testid={`rr-row-${rr.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-3 p-3 text-left hover:bg-muted/50 transition"
      >
        <div className="mt-1">
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <RRTypeBadge value={rr.type} testId={`rr-row-type-${rr.id}`} />
            <Badge variant="outline" className="text-xs">{stageLabel(stages, rr.stage_code)}</Badge>
            <span className="text-sm font-medium">{rr.qty_affected} pcs {rr.qty_target_type === "CELANA" ? "celana" : "atasan"}</span>
            <RRStatusBadge value={rr.status} />
            {rr.persetujuan_kc && (
              <Badge variant="outline" className="text-xs" data-testid={`rr-persetujuan-${rr.id}`}>
                Persetujuan {rr.penyetuju === "OWNER" ? "Owner" : "KC"}: {({ MENUNGGU: "menunggu", DISETUJUI: "disetujui", DITOLAK: "ditolak" })[rr.persetujuan_kc] || rr.persetujuan_kc}
              </Badge>
            )}
            {rr.evidence_count > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Paperclip className="h-3 w-3" /> {rr.evidence_count}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            <span className="font-medium">{CAUSE_LABELS[rr.cause_code] || rr.cause_code}</span>
            {rr.cause_notes && <span> · {rr.cause_notes}</span>}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">Dilaporkan {fmtDate(rr.reported_at)}</div>
        </div>
      </button>

      {expanded && (
        <div className="border-t px-4 py-3 bg-muted/50 space-y-4">
          {loadingDetail ? (
            <p className="text-xs text-muted-foreground">Memuat detail…</p>
          ) : detail ? (
            <>
              {terminal && (
                <div className="text-xs bg-card border rounded p-2">
                  <div className="font-medium mb-1">
                    {rr.status === "RESOLVED" ? "Selesai" : "Dibatalkan"} — {fmtDate(detail.resolved_at)}
                  </div>
                  <div className="text-muted-foreground whitespace-pre-wrap">{detail.resolution_notes || "-"}</div>
                </div>
              )}

              {/* Actions (only for non-terminal + write role) */}
              {canWrite && !terminal && (
                <div className="flex flex-wrap gap-2">
                  {/* BUSINESS-21: remake dari QC packing diputus Kepala Cabang region dulu;
                      BUSINESS-23: bila pelapornya KC region itu sendiri, Owner yang memutus. */}
                  {rr.persetujuan_kc === "MENUNGGU" && (rr.penyetuju === "OWNER" ? isOwner : isKepalaCabang) && (
                    <PersetujuanKcButtons woId={woId} rrId={rr.id} onDone={onChange} />
                  )}
                  {rr.type === "REMAKE" && rr.status === "REPORTED" && (!rr.persetujuan_kc || rr.persetujuan_kc === "DISETUJUI") && (
                    <StartRemakeButton woId={woId} rrId={rr.id} onDone={onChange} />
                  )}
                  <ResolveButton woId={woId} rrId={rr.id} onDone={onChange} />
                  <CancelButton woId={woId} rrId={rr.id} onDone={onChange} />
                </div>
              )}

              {/* Evidence */}
              <div>
                <div className="text-xs font-medium mb-2">Bukti foto</div>
                <EvidenceManager
                  woId={woId}
                  rrId={rr.id}
                  evidence={detail.evidence || []}
                  canWrite={canWrite && !terminal}
                  canDelete={canDelete}
                  onChange={() => { loadDetail(); onChange(); }}
                />
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

function PersetujuanKcButtons({ woId, rrId, onDone }) {
  const [busy, setBusy] = useState(false);
  const putus = async (setuju) => {
    let catatan = null;
    if (!setuju) {
      catatan = (await mintaTeks("Alasan menolak remake (wajib):")) || "";
      if (!catatan.trim()) return;
    }
    setBusy(true);
    try {
      await rrApi.persetujuanKc(woId, rrId, setuju, catatan);
      toast.success(setuju ? "Remake disetujui." : "Remake ditolak.");
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal."));
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" disabled={busy} onClick={() => putus(true)} data-testid={`rr-setujui-${rrId}`}>Setujui remake</Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => putus(false)} data-testid={`rr-tolak-${rrId}`}>Tolak remake</Button>
    </>
  );
}

function StartRemakeButton({ woId, rrId, onDone }) {
  const [busy, setBusy] = useState(false);
  const doAct = async () => {
    setBusy(true);
    try {
      await rrApi.startRemake(woId, rrId);
      toast.success("Remake dimulai.");
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal."));
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" disabled={busy} onClick={doAct} data-testid={`rr-start-remake-${rrId}`}>
      {busy ? "…" : "Mulai Remake"}
    </Button>
  );
}

function ResolveButton({ woId, rrId, onDone }) {
  const [notes, setNotes] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const doAct = async () => {
    setBusy(true);
    try {
      await rrApi.resolve(woId, rrId, notes);
      toast.success("Reject/remake selesai.");
      setOpen(false); setNotes("");
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal."));
    } finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button size="sm" className="bg-success text-success-foreground hover:bg-success/90" data-testid={`rr-resolve-${rrId}`}>
          Selesaikan
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Selesaikan reject/remake?</AlertDialogTitle>
          <AlertDialogDescription>Berikan catatan resolusi (min 5 karakter).</AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea
          value={notes} onChange={(e) => setNotes(e.target.value)}
          rows={3} placeholder="Catatan resolusi…"
          data-testid={`rr-resolve-notes-${rrId}`}
        />
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Batal</AlertDialogCancel>
          <AlertDialogAction
            disabled={notes.trim().length < 5 || busy}
            onClick={(e) => { e.preventDefault(); doAct(); }}
            data-testid={`rr-resolve-confirm-${rrId}`}
          >Konfirmasi Selesai</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function CancelButton({ woId, rrId, onDone }) {
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const doAct = async () => {
    setBusy(true);
    try {
      await rrApi.cancel(woId, rrId, reason);
      toast.success("Reject/remake dibatalkan.");
      setOpen(false); setReason("");
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal."));
    } finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="destructive" data-testid={`rr-cancel-${rrId}`}>
          Batalkan
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Batalkan laporan reject/remake?</AlertDialogTitle>
          <AlertDialogDescription>Berikan alasan pembatalan (min 5 karakter).</AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea
          value={reason} onChange={(e) => setReason(e.target.value)}
          rows={3} placeholder="Alasan pembatalan…"
          data-testid={`rr-cancel-reason-${rrId}`}
        />
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Batal</AlertDialogCancel>
          <AlertDialogAction
            disabled={reason.trim().length < 5 || busy}
            onClick={(e) => { e.preventDefault(); doAct(); }}
            data-testid={`rr-cancel-confirm-${rrId}`}
          >Konfirmasi Batal</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
